import React from 'react';
import { type MessageDescriptor, useIntl, FormattedMessage } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import Button from '@atlaskit/button';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { EditorActions } from '@atlaskit/editor-core';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { QuickCardContainer, ButtonText, QuickCardIcon, QuickCardColumn } from './bpqaStyled.tsx';
import { QuickActionIcon } from './QuickActionIcon.tsx';
import type { PresetAPIType } from './bpqaConst.ts';

export type QuickActionType = {
	id: string;
	testId: string;
	name: MessageDescriptor;
	alt: MessageDescriptor;
	adf: ADFEntity[];
	title?: MessageDescriptor;
	shortName: string;
	type: string;
	onClick?: (editorView?: EditorView, editorPresetAPI?: PresetAPIType) => void;
};

export type QuickActionCardProps = {
	quickAction: QuickActionType;
	editorActions: EditorActions;
	disabled: boolean;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	focusEditor: any;
	editorPresetAPI?: PresetAPIType;
};

export const QuickActionCard = ({
	quickAction,
	editorActions,
	disabled,
	createAnalyticsEvent,
	focusEditor,
	editorPresetAPI,
}: QuickActionCardProps) => {
	const intl = useIntl();

	const dataTestId = quickAction.testId;

	const formatADF = (adfEntities: ADFEntity[]) => {
		// We are checking if the adf type is an extension,
		// and if so, overriding the title to use the
		// internationalized version
		return adfEntities.map((adfEntity) => {
			if (adfEntity.type === 'extension') {
				const updatedAdf = adfEntity;
				if (updatedAdf.attrs?.parameters?.macroMetadata?.title && quickAction.title) {
					updatedAdf.attrs.parameters.macroMetadata.title = intl.formatMessage(quickAction.title);
				}
				return updatedAdf;
			}
			return adfEntity;
		});
	};

	const fireBPQAAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'QuickActionCard',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'blankPageQuickAction',
				attributes: {
					quickAction: quickAction.id,
					version: '2',
				},
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'inserted',
				actionSubject: quickAction.type,
				actionSubjectId: quickAction.shortName,
				source: 'BPQA',
				attributes: {
					version: '2',
				},
			},
		}).fire();
	};

	const handleOnClick = () => {
		const editorView = editorActions._privateGetEditorView();

		if (quickAction.adf.length) {
			editorActions.replaceSelection(formatADF(quickAction.adf));
		}
		quickAction.onClick?.(editorView, editorPresetAPI);
		focusEditor();

		fireBPQAAnalytics();
	};

	return (
		<QuickCardContainer data-testid={`${dataTestId}-card`} isDisabled={disabled}>
			<Button
				spacing="compact"
				testId={`${dataTestId}-card-button`}
				isDisabled={disabled}
				onClick={handleOnClick}
				shouldFitContainer
				iconBefore={
					<QuickCardIcon isDisabled={disabled}>
						<QuickActionIcon quickActionId={quickAction.id} />
					</QuickCardIcon>
				}
			>
				<QuickCardColumn>
					<ButtonText data-testid={`${dataTestId}-card-name`} isDisabled={disabled}>
						<FormattedMessage {...quickAction.name} />
					</ButtonText>
				</QuickCardColumn>
			</Button>
		</QuickCardContainer>
	);
};
